import React from 'react';

import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

/**
 * A wide drawer for something that used to be a page.
 *
 * Work and Insights were both routes, which made each of them somewhere to
 * remember to visit — the wrong shape for surfaces whose whole value is
 * telling you something needs attention. They open over whatever the vendor
 * was already looking at instead, so the context they refer to is still on
 * screen behind them.
 *
 * Wider than the stock sheet on purpose: `sm:max-w-sm` is right for a detail
 * pane about one record and far too narrow for a grid of cards.
 */
export const SidePanel: React.FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: string;
    /**
     * Tailwind max-width for the panel.
     *
     * Must carry the `data-[side=right]:sm:` prefix, not a bare `sm:`. The
     * sheet sets its own `data-[side=right]:sm:max-w-sm`, and to tailwind-merge
     * those are different keys — so a plain `sm:max-w-[820px]` did not replace
     * it, both survived, and the more specific one won. Every drawer in the app
     * was 384px wide, which read as a design problem rather than a bug.
     */
    width?: string;
    children: React.ReactNode;
}> = ({ open, onOpenChange, title, description, width = 'data-[side=right]:sm:max-w-[760px]', children }) => (
    <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className={cn('w-full gap-0 p-0', width)}>
            <SheetHeader className="border-b p-6 pe-14">
                <SheetTitle>{title}</SheetTitle>
                {description && <SheetDescription>{description}</SheetDescription>}
            </SheetHeader>
            {/* The body scrolls, not the panel: the header stays put while a
                long queue is read, which is the point of a drawer over a page. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
        </SheetContent>
    </Sheet>
);
